















































































































































































import Vue from "vue";
import dayjs from "dayjs";
import eventBus from "@/services/eventBus";
import base64toUInt8Array, { openPdfInNewTab } from "@/utils/fileConvert";
import DateInput from "@/components/DateInput.vue";
import LookupComboBox from "@/modules/transactions/components/LookupComboBox.vue";
import api from "../services/api";
import {
  BookingEventsRequest,
  DefaultBookingEventsRequest,
} from "../models/BookingEvents";
import { t } from "@/plugins/i18n";

// A LookupComboBox textField-je string mezőt vár — az id szöveges tükrével bővített lookup-elem.
interface TransactionOption {
  id: number;
  accEvent: string;
  idText: string;
}

// Napló/állapot választó-elem (K2D d_g_boktype / d_g_transstat sorai).
interface SelectableLookupRow {
  id: number;
  name: string;
  // K2D d_g_boktype: a napló-sor a nicname rövid kódot mutatja (KI/BE/...), nem a számot.
  nicName?: string | null;
  defaultSelected: boolean;
  selected: boolean;
}

interface Data {
  filter: BookingEventsRequest;
  bookTypeRows: SelectableLookupRow[];
  statusRows: SelectableLookupRow[];
  selectedBookTypeIds: number[];
  selectedStatusIds: number[];
  transactionOptions: TransactionOption[];
  printing: boolean;
}
interface Methods {
  loadLookups(): Promise<void>;
  loadTransactions(): Promise<void>;
  applyDefaults(): void;
  syncSelectedIds(): void;
  bookTypeLabel(row: SelectableLookupRow): string;
  removeBookType(id: number): void;
  removeStatus(id: number): void;
  onTransIdChange(field: "transIdFrom" | "transIdTo", item: TransactionOption | null): void;
  clearTransInterval(): void;
  printPdf(): Promise<void>;
  reset(): void;
}
interface Computed {
  transIntervalActive: boolean;
  sortMode: string;
}

export default Vue.extend<Data, Methods, Computed, {}>({
  components: { DateInput, LookupComboBox },

  data(): Data {
    return {
      filter: {
        ...DefaultBookingEventsRequest,
        // K2D-ben a dátumok üresen indulnak; itt alapból a tárgyhó, hogy ne fusson szűrés nélkül
        accDateFrom: dayjs().startOf("month").format("YYYY-MM-DD"),
        accDateTo: dayjs().endOf("month").format("YYYY-MM-DD"),
      },
      bookTypeRows: [],
      statusRows: [],
      selectedBookTypeIds: [],
      selectedStatusIds: [],
      transactionOptions: [],
      printing: false,
    };
  },

  async beforeMount() {
    await Promise.all([this.loadLookups(), this.loadTransactions()]);
  },

  computed: {
    // K2D pb_ok: ha van TR-intervallum, csak az érvényesül
    transIntervalActive(): boolean {
      return this.filter.transIdFrom > 0 || this.filter.transIdTo > 0;
    },
    // a rendezés-rádió string értéket vár — a filter.sortBySupplier boolean tükre
    sortMode: {
      get(): string {
        return this.filter.sortBySupplier ? "supplier" : "date";
      },
      set(value: string) {
        this.filter.sortBySupplier = value === "supplier";
      },
    },
  },

  methods: {
    async loadLookups() {
      try {
        const lookups = await api.GetBookingEventsLookups();
        this.bookTypeRows = lookups.bookTypes.map(x => ({
          id: x.id,
          name: x.name,
          nicName: x.nicName ? x.nicName.trim() : "",
          defaultSelected: x.defaultSelected,
          selected: x.defaultSelected,
        }));
        this.statusRows = lookups.statuses.map(x => ({
          id: x.id,
          name: x.name,
          defaultSelected: x.defaultSelected,
          selected: x.defaultSelected,
        }));
        this.applyDefaults();
      } catch (error) {
        console.error(error);
        eventBus.$emit("error", t("GeneralLedger_ASzuroListakBetolteseNemSikerult"));
      }
    },

    async loadTransactions() {
      try {
        this.transactionOptions = (await api.GetBookingEventsTransactions()).map(x => ({
          id: x.id,
          accEvent: x.accEvent,
          idText: String(x.id),
        }));
      } catch (error) {
        console.error(error);
        eventBus.$emit("error", t("GeneralLedger_ATranzakcioListaBetolteseNemSikerult"));
      }
    },

    // K2D retrieveend: napló mind, állapot csak a 6-nál kisebb kódúak
    applyDefaults() {
      this.selectedBookTypeIds = this.bookTypeRows.filter(r => r.defaultSelected).map(r => r.id);
      this.selectedStatusIds = this.statusRows.filter(r => r.defaultSelected).map(r => r.id);
      this.syncSelectedIds();
    },

    syncSelectedIds() {
      this.filter.bookTypeIds = [...this.selectedBookTypeIds];
      this.filter.statusIds = [...this.selectedStatusIds];
    },

    // Napló-elem felirata: rövid kód (nicname) + megnevezés
    bookTypeLabel(row: SelectableLookupRow): string {
      return row.nicName ? `${row.nicName} — ${row.name}` : row.name;
    },

    // A chipen lévő x: egy elem kivétele a kijelölésből
    removeBookType(id: number) {
      if (this.transIntervalActive) return;
      this.selectedBookTypeIds = this.selectedBookTypeIds.filter(x => x !== id);
      this.syncSelectedIds();
    },

    removeStatus(id: number) {
      if (this.transIntervalActive) return;
      this.selectedStatusIds = this.selectedStatusIds.filter(x => x !== id);
      this.syncSelectedIds();
    },

    // A lookupból választott sor Tranzakció ID-ja kerül az intervallum-mezőbe
    onTransIdChange(field: "transIdFrom" | "transIdTo", item: TransactionOption | null) {
      this.filter[field] = item ? Number(item.idText) || 0 : 0;
      // K2D dw_1.itemchanged: ha a pár másik fele üres, automatikusan a beírt értéket kapja
      // (ig := szam ill. szam := ig) — így a fél-oldalas kitöltés egyetlen tranzakciót jelent.
      const other = field === "transIdFrom" ? "transIdTo" : "transIdFrom";
      if (this.filter[field] > 0 && !(this.filter[other] > 0)) {
        this.filter[other] = this.filter[field];
      }
    },

    clearTransInterval() {
      this.filter.transIdFrom = 0;
      this.filter.transIdTo = 0;
    },

    async printPdf() {
      if (!this.transIntervalActive) {
        const hasAccDate = !!this.filter.accDateFrom && !!this.filter.accDateTo;
        const hasBookingDate = !!this.filter.bookingDateFrom && !!this.filter.bookingDateTo;
        if (!hasAccDate && !hasBookingDate) {
          eventBus.$emit("error", t("GeneralLedger_AdjonMegIdoszakotVagyTranzakcioAzonosito"));
          return;
        }
        if (this.filter.statusIds.length === 0) {
          eventBus.$emit("error", t("GeneralLedger_ValasszonLegalabbEgyAllapotot"));
          return;
        }
        if (this.filter.bookTypeIds.length === 0) {
          eventBus.$emit("error", t("GeneralLedger_ValasszonLegalabbEgyNaplotipust"));
          return;
        }
      }

      this.printing = true;
      eventBus.$emit("busy-message", true);
      try {
        const result = await api.GetBookingEventsPdf({
          ...this.filter,
          transIdFrom: this.filter.transIdFrom || 0,
          transIdTo: this.filter.transIdTo || 0,
        });
        if (!result.success || !result.pdf) {
          // K2D "NINCS" ág: nincs a feltételeknek megfelelő adat
          eventBus.$emit("error", result.message || t("GeneralLedger_ARiportElkesziteseNemSikerult"));
          return;
        }
        openPdfInNewTab(base64toUInt8Array(result.pdf));
      } catch (error) {
        console.error(error);
        eventBus.$emit("error", t("GeneralLedger_ARiportElkesziteseNemSikerult"));
      } finally {
        this.printing = false;
        eventBus.$emit("busy-message", false);
      }
    },

    // K2D pb_urit
    reset() {
      this.filter = {
        ...DefaultBookingEventsRequest,
        accDateFrom: dayjs().startOf("month").format("YYYY-MM-DD"),
        accDateTo: dayjs().endOf("month").format("YYYY-MM-DD"),
      };
      this.applyDefaults();
    },
  },
});
